import type { MaterialKind } from "../../../shared/api-contract";
import type { ConfiguratorOption } from "../../../features/vault/configurator";
import { libraryItemId } from "../../../features/vault/applyOption";
import type { PresetMark } from "../basic/applyEngine";
import { markKind } from "../basic/markKind";

/**
 * A configurator choice in the Basic engine's vocabulary: the mark
 * `applyLibraryMaterial` puts on a part. The engine's `putMaterial` then makes
 * the same calls `applyOption` makes for the shared link (reclassify first; the
 * real diamond shader under a gem file) — `verify:configurator` checks the two
 * agree. Null for a legacy colour-only metal, which is a look value, not a mark.
 */
export function optionMark(option: ConfiguratorOption): PresetMark | null {
  const p = option.preset;
  // `presetId` is what Basic's material panels ring as chosen: the library
  // item's own id, so trying a choice here rings the same tile there.
  const libId = libraryItemId(option.id) ?? option.id;
  if (p?.url) return { presetId: libId, url: p.url, category: p.kind, ...(p.format === "dmat" ? { dmat: true } : {}) };
  if (p?.presetId) {
    return p.kind === "metal"
      ? { presetId: p.presetId, finishId: p.finishId ?? "polished", category: "metal" }
      : { presetId: p.presetId, category: p.kind };
  }
  if (option.gemPresetId) return { presetId: option.gemPresetId, category: "gem" };
  return null;
}

/**
 * Would putting `option` on a part of `slotKind` leave it the OTHER kind — a
 * metal file, a ceramic or a pearl on the stones, a diamond on the band? The
 * engine's own rule (`markKind`), asked before anything is applied, so the
 * "+" picker can say so first. A legacy colour changes no part's kind.
 */
export function crossesKind(option: ConfiguratorOption, slotKind: MaterialKind): boolean {
  const mark = optionMark(option);
  return !!mark && markKind(mark, slotKind) !== slotKind;
}
